import React, { ReactElement, useContext, useState } from 'react';
import styled from 'styled-components';
import { t } from 'i18next';
import { GiveawayResponse, getGiveawayAssets } from '@oneaudi/avp-hck-dga-customerui-commons';
import { screen } from '../../common/breakpoints';
import { GiveawayContext } from '../../services/GiveawayContextService';
import { HorizontalSpacing, VerticalSpacing } from '../../common/spacings';
import { ExteriorThreeSixtyView } from './vw-360-exterior-view/exteriorThreeSixtyView';
import { CLUSTER_DATA } from '../clusterButton/ClusterButton.types';
import { CLUSTER_360_VIEW } from '../../enums/ClusterButton';
import PanoramaViewer from '../panoramaViewer/PanoramaViewer';
import { ClusteredButtonContainer } from '../clusterButton/ClusterButtonContainer';
import { ReactComponent as CarFront } from '../../assets/icons/car-front.svg';
import { ReactComponent as CarSeat } from '../../assets/icons/car-seat.svg';

const StyledTurnTableContainer = styled.div`
  width: inherit;
  display: flex;
  flex-direction: column;
`;

const StyledTurnTableHeading = styled.div<{ visible?: boolean }>`
  display: ${(props) => (props.visible ? 'inline' : 'none')};
  margin-left: 0.3em;
  padding-bottom: ${VerticalSpacing.S};
  padding-top: ${VerticalSpacing.S};
  padding-left: ${HorizontalSpacing.S};

  @media screen and ${screen.m} {
    padding-left: ${HorizontalSpacing.M};
    padding-bottom: ${VerticalSpacing.M};
    padding-top: ${VerticalSpacing.M};
  }

  @media screen and ${screen.l} {
    padding-left: ${HorizontalSpacing.L};
    padding-bottom: ${VerticalSpacing.L};
    padding-top: ${VerticalSpacing.L};
  }

  @media screen and ${screen.xl} {
    padding-left: ${HorizontalSpacing.XL};
    padding-bottom: s ${VerticalSpacing.XL};
    padding-top: ${VerticalSpacing.XL};
  }

  @media screen and ${screen.xxl} {
    padding-left: ${HorizontalSpacing.XL};
    padding-bottom: ${VerticalSpacing.XL};
    padding-top: ${VerticalSpacing.XL};
  }
`;

const PanoramaWrapper = styled.div`
  aspect-ratio: 16 / 9;
`;

const WrapperDiv = styled.div`
  position: relative;
`;

const StyledClusterControls = styled.div`
  display: flex;
  justify-content: center;
  padding-top: ${HorizontalSpacing.XS};
  padding-bottom: ${HorizontalSpacing.S};

  @media screen and (${screen.m}) {
    display: flex;
    justify-content: end;
    z-index: 1;
    align-items: end;
    position: absolute;
    bottom: 0;
    right: 0;
    left: 0;
    top: 0;
    pointer-events: none;
    padding: ${HorizontalSpacing.S} ${VerticalSpacing.XL};
  }
`;

export const Turntable = (): ReactElement => {
  const giveawayData: GiveawayResponse = useContext(GiveawayContext);
  const imageUrls = getGiveawayAssets(giveawayData, 'turntable');
  const [panoramaImage] = getGiveawayAssets(giveawayData, 'interior-pano');

  const [activeTab, setActiveTab] = useState<string>(CLUSTER_360_VIEW.TURNTABLE);

  if (imageUrls.length === 0) {
    return <></>;
  }

  const tabData: CLUSTER_DATA[] = [
    {
      name: CLUSTER_360_VIEW.TURNTABLE,
      icon: (
        <CarFront
          aria-label={t('clusterButtons.turntable.ariaLabel')}
          title={t('clusterButtons.turntable.title')}
          width="24px"
          height="24px"
        />
      ),
    },
    {
      name: CLUSTER_360_VIEW.INTERIOR_360,
      icon: (
        <CarSeat
          aria-label={t('clusterButtons.interior.ariaLabel')}
          title={t('clusterButtons.interior.title')}
          width="24px"
          height="24px"
        />
      ),
    },
  ];

  const handleTabChange = (label: string) => {
    setActiveTab(label);
  };

  return (
    <WrapperDiv>
      <StyledTurnTableContainer id="turntable" data-testid="turntable-container">
        <StyledTurnTableHeading
          visible={giveawayData.renderCameraFlight}
          data-testid="turntable-title"
        >
          <h2>{t('body.details.turntable.title')}</h2>
          <p>{t('body.details.turntable.subtitle')}</p>
        </StyledTurnTableHeading>
        {activeTab === CLUSTER_360_VIEW.TURNTABLE ? (
          <ExteriorThreeSixtyView assetUrls={imageUrls} />
        ) : (
          <PanoramaWrapper>
            <PanoramaViewer image={panoramaImage} />
          </PanoramaWrapper>
        )}
      </StyledTurnTableContainer>
      {panoramaImage && (
        <StyledClusterControls>
          <ClusteredButtonContainer
            tabs={tabData}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </StyledClusterControls>
      )}
    </WrapperDiv>
  );
};
